import React, { createContext, useContext, useReducer, type Dispatch, type ReactNode } from 'react';
import { DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';
import type { 
  CaptureSequence, 
  CapturedImage, 
  Settings, 
  OverlayState, 
  UnassignedImage, 
  AnalysisData, 
  Model3D, 
  AnalysisQueueItem 
} from '../types';

// State Definition
export interface PostureState {
  // Workflow
  workflowStep: 'capture' | 'analyze' | 'compare' | 'report';
  currentView: CaptureSequence | null;
  captureMode: 'guided' | 'manual';
  
  // Capture
  capturedImages: Record<CaptureSequence, CapturedImage | null>;
  unassignedImages: UnassignedImage[];
  isCameraActive: boolean;
  analysisQueue: AnalysisQueueItem[];
  
  // Analysis
  analysisData: AnalysisData;
  model3D: Model3D | null;
  selectedLandmark: number | null;
  
  // Settings
  settings: Settings;
  overlayState: OverlayState;
  
  // UI State
  isLoading: boolean;
  loaderText: string;
  loaderSubText: string;
}

// Initial State
export const INITIAL_STATE: PostureState = {
  workflowStep: 'capture',
  currentView: null,
  captureMode: 'guided',
  capturedImages: { Anterior: null, Posterior: null, Left: null, Right: null },
  unassignedImages: [],
  isCameraActive: false,
  analysisQueue: [],
  analysisData: {},
  model3D: null,
  selectedLandmark: null,
  settings: { unit: 'cm', clientHeightCM: 0, postureOrientationStrategyId: DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID },
  overlayState: {
    Anterior: { client: true }, Posterior: { client: true },
    Left: { client: true }, Right: { client: true },
  },
  isLoading: true,
  loaderText: 'Loading AI Vision…',
  loaderSubText: 'First load ~5–10s while caching WASM; faster next runs.',
};

// Actions
export type PostureAction = 
  | { type: 'SET_WORKFLOW_STEP'; payload: PostureState['workflowStep'] }
  | { type: 'SET_CURRENT_VIEW'; payload: CaptureSequence | null }
  | { type: 'SET_CAPTURE_MODE'; payload: 'guided' | 'manual' }
  | { type: 'UPDATE_CAPTURED_IMAGE'; payload: { view: CaptureSequence; image: CapturedImage | null } }
  | { type: 'SET_UNASSIGNED_IMAGES'; payload: UnassignedImage[] }
  | { type: 'SET_CAMERA_ACTIVE'; payload: boolean }
  | { type: 'ENQUEUE_ANALYSIS'; payload: AnalysisQueueItem }
  | { type: 'DEQUEUE_ANALYSIS' }
  | { type: 'UPDATE_ANALYSIS_DATA'; payload: AnalysisData }
  | { type: 'SET_MODEL_3D'; payload: Model3D | null }
  | { type: 'SELECT_LANDMARK'; payload: number | null }
  | { type: 'UPDATE_SETTINGS'; payload: Partial<Settings> }
  | { type: 'TOGGLE_OVERLAY'; payload: CaptureSequence }
  | { type: 'SET_LOADING'; payload: { isLoading: boolean; text?: string; subText?: string } }
  | { type: 'RESET_STATE' };

// Reducer
function postureReducer(state: PostureState, action: PostureAction): PostureState {
  switch (action.type) {
    case 'SET_WORKFLOW_STEP':
      return { ...state, workflowStep: action.payload };
    case 'SET_CURRENT_VIEW':
      return { ...state, currentView: action.payload };
    case 'SET_CAPTURE_MODE':
      return { ...state, captureMode: action.payload };
    case 'UPDATE_CAPTURED_IMAGE':
      return {
        ...state,
        capturedImages: {
          ...state.capturedImages,
          [action.payload.view]: action.payload.image
        }
      };
    case 'SET_UNASSIGNED_IMAGES':
      return { ...state, unassignedImages: action.payload };
    case 'SET_CAMERA_ACTIVE':
      return { ...state, isCameraActive: action.payload };
    case 'ENQUEUE_ANALYSIS':
      return { ...state, analysisQueue: [...state.analysisQueue, action.payload] };
    case 'DEQUEUE_ANALYSIS':
      return { ...state, analysisQueue: state.analysisQueue.slice(1) };
    case 'UPDATE_ANALYSIS_DATA':
      return { ...state, analysisData: { ...state.analysisData, ...action.payload } };
    case 'SET_MODEL_3D':
      return { ...state, model3D: action.payload };
    case 'SELECT_LANDMARK':
      return { ...state, selectedLandmark: action.payload };
    case 'UPDATE_SETTINGS':
      return { ...state, settings: { ...state.settings, ...action.payload } };
    case 'TOGGLE_OVERLAY':
      return {
        ...state,
        overlayState: {
          ...state.overlayState,
          [action.payload]: { client: !state.overlayState[action.payload].client }
        }
      };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload.isLoading,
        loaderText: action.payload.text || state.loaderText,
        loaderSubText: action.payload.subText || state.loaderSubText
      };
    case 'RESET_STATE':
      return { ...INITIAL_STATE, isLoading: false, settings: state.settings }; // Keep settings
    default:
      return state;
  }
}

// Context
const PostureContext = createContext<{
  state: PostureState;
  dispatch: Dispatch<PostureAction>;
} | undefined>(undefined);

export const PostureProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(postureReducer, INITIAL_STATE);

  return (
    <PostureContext.Provider value={{ state, dispatch }}>
      {children}
    </PostureContext.Provider>
  );
};

// Hook
export const usePosture = () => {
  const context = useContext(PostureContext);
  if (!context) {
    throw new Error('usePosture must be used within a PostureProvider');
  }
  return context;
};

